import { NavLink, useParams } from 'react-router-dom';
import { LogOut, Pin, PinOff, X } from 'lucide-react';
import { enabledModules } from '@/modules/registry';
import { useAuth } from '@/contexts/AuthContext';
import { useAutoCollapse } from '@/hooks/useAutoCollapse';
import { cn } from '@/lib/utils';
import w3Logo from '@/assets/w3-logo-official.png';

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

/**
 * Sidebar navigazione moduli — drawer sotto lg; sopra lg è collassabile a
 * rail icone con hover-expand, auto-collapse dopo 30s di inattività e pin
 * che blocca l'espansione (persistito).
 */
export function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const { slug } = useParams<{ slug: string }>();
  const { user, logout } = useAuth();
  const modules = enabledModules();
  const { collapsed, pinned, togglePinned, touch, expand, collapse } = useAutoCollapse('w3.sidebar.pinned');

  const displayName =
    [user?.firstName ?? user?.first_name, user?.lastName ?? user?.last_name]
      .filter(Boolean)
      .join(' ') || user?.email || '';

  // Su mobile il drawer è sempre "espanso"; il collasso vale solo desktop.
  const railMode = collapsed && !mobileOpen;

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r border-border transition-all duration-300 lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0 w-64' : '-translate-x-full',
          railMode ? 'lg:w-16' : 'lg:w-64'
        )}
        onMouseEnter={railMode ? expand : undefined}
        onMouseLeave={railMode || pinned ? undefined : collapse}
        onMouseMove={railMode ? undefined : touch}
        onKeyDown={railMode ? undefined : touch}
      >
        <div
          className={cn(
            'flex items-center h-16 border-b border-border shrink-0',
            railMode ? 'justify-center px-0' : 'gap-2 px-5'
          )}
        >
          <img src={w3Logo} alt="W3" className="h-8 w-auto mix-blend-multiply dark:mix-blend-normal" />
          {!railMode && (
            <>
              <span className="text-xl font-bold text-w3-orange tracking-tight">Suite</span>
              <button
                type="button"
                onClick={togglePinned}
                className={cn(
                  'ml-auto hidden lg:inline-flex p-1.5 rounded-md transition-colors',
                  pinned
                    ? 'text-w3-orange bg-w3-orange/10 hover:bg-w3-orange/20'
                    : 'text-muted-foreground hover:text-w3-orange hover:bg-w3-orange/10'
                )}
                title={pinned ? 'Sblocca (auto-collapse attivo)' : 'Blocca aperta'}
                aria-label={pinned ? 'Sblocca sidebar' : 'Blocca sidebar aperta'}
              >
                {pinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="ml-auto lg:hidden p-1.5 rounded-md text-muted-foreground hover:bg-muted"
                aria-label="Chiudi menu"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        <nav className={cn('flex-1 overflow-y-auto py-3 space-y-0.5', railMode ? 'px-2' : 'px-3')}>
          {modules.map((mod) => {
            const Icon = mod.icon;
            return (
              <NavLink
                key={mod.id}
                to={`/${slug}/${mod.route}`}
                onClick={onClose}
                title={railMode ? mod.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center rounded-lg text-sm font-medium transition-all duration-150',
                    railMode ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2 border-l-2',
                    isActive
                      ? railMode
                        ? 'bg-w3-orange/10 text-w3-orange'
                        : 'bg-w3-orange/10 text-w3-orange border-w3-orange'
                      : railMode
                        ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted border-transparent'
                  )
                }
              >
                <Icon className="w-[18px] h-[18px] shrink-0" />
                {!railMode && <span>{mod.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className={cn('border-t border-border shrink-0', railMode ? 'p-2' : 'p-3')}>
          <div className={cn('flex items-center', railMode ? 'justify-center' : 'gap-3 px-2')}>
            <div className="w-8 h-8 rounded-full w3-gradient-brand flex items-center justify-center text-white text-xs font-semibold shrink-0">
              {(displayName || '?').slice(0, 1).toUpperCase()}
            </div>
            {!railMode && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{displayName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="Esci"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
